import { create } from 'zustand';
import authService, { User } from '@/services/auth.service';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isImpersonating: boolean;
  originalAdmin: User | null;
  setUser: (user: User | null) => void;
  setIsAuthenticated: (value: boolean) => void;
  setIsLoading: (value: boolean) => void;
  checkAuth: () => Promise<void>;
  logout: () => Promise<void>;
  startImpersonation: (targetUser: User) => void;
  stopImpersonation: () => void;
}

const useAuthStore = create<AuthState>((set, get) => ({
  user: authService.getCurrentUser(),
  isAuthenticated: authService.isAuthenticated(),
  isLoading: false,
  isImpersonating: authService.isImpersonating(),
  originalAdmin: authService.isImpersonating()
    ? (() => {
        try {
          const adminUser = sessionStorage.getItem('admin_user');
          return adminUser ? JSON.parse(adminUser) : null;
        } catch {
          return null;
        }
      })()
    : null,

  setUser: (user) => set({ user }),
  setIsAuthenticated: (isAuthenticated) => set({ isAuthenticated }),
  setIsLoading: (isLoading) => set({ isLoading }),

  checkAuth: async () => {
    try {
      if (authService.isAuthenticated()) {
        const { user } = await authService.getProfile();
        set({ user, isAuthenticated: true });
      } else {
        set({ user: null, isAuthenticated: false });
      }
    } catch (error: any) {
      // Nur echte Auth-Fehler beenden die Session. Rate-Limits (429),
      // Serverfehler (5xx) und Netzwerkausfälle dürfen den User NICHT ausloggen —
      // sonst wirft ein einzelner 429 auf /auth/me den eingeloggten User raus.
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        set({ user: null, isAuthenticated: false });
      }
      // sonst: bestehenden Zustand beibehalten
    }
  },

  logout: async () => {
    await authService.logout();
    set({ user: null, isAuthenticated: false });
    window.location.href = '/login';
  },

  startImpersonation: (targetUser: User) => {
    const currentUser = get().user;
    set({
      originalAdmin: currentUser,
      isImpersonating: true,
      user: targetUser,
      isAuthenticated: true,
    });
  },

  stopImpersonation: () => {
    authService.stopImpersonation();
    const adminUser = authService.getCurrentUser();
    set({
      user: adminUser,
      isImpersonating: false,
      originalAdmin: null,
      isAuthenticated: true,
    });
    window.location.href = '/tickets';
  },
}));

export default useAuthStore;
